import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { isEqual, noop } from 'lodash';

import { IItem } from '@rbi-ctg/menu';
import { getUserModifications } from 'components/product-detail/product-customization/utils';
import { useFlag } from 'state/launchdarkly';
import {
  IProductWizardContext,
  IProductWizardProvider,
  IUserManualModifications,
  IUserSelection,
  Product,
  ProductMenuObject,
  ProductWizardStep,
  SubmitCustomizationsFn,
  UserSelectionComboSlot,
  UserSelectionPickerAspect,
} from 'state/product-wizard/types';
import { ProductWizardUtils } from 'state/product-wizard/utils';
import { MIN_CART_QUANTITY } from 'utils/cart';
import { LaunchDarklyFlag } from 'utils/launchdarkly';

import { useEditingCartEntry } from './hooks/use-editing-cart-entry';

const ProductWizardContext = createContext<IProductWizardContext>({
  currentStep: ProductWizardStep.ProductHome,
  goToCustomizeProduct: () => null,
  goToHome: () => null,
  itemToCustomize: null,
  menuObject: {} as ProductMenuObject,
  productQuantity: MIN_CART_QUANTITY,
  selectedProduct: {} as Product,
  setProductQuantity: noop,
  slotKeyToCustomize: '',
  submitCustomizations: () => null,
  updatePickerAspectSelections: () => null,
  userSelections: {} as IUserSelection,
  manualModifications: {} as IUserManualModifications,
  isDirty: false,
});

export const ProductWizardProvider: React.FC<IProductWizardProvider> = ({
  product: menuObject,
  children,
}) => {
  const prevMenuObjectRef = useRef(menuObject);
  const editingCartEntry = useEditingCartEntry();
  const [currentStep, setCurrentStep] = useState<ProductWizardStep>(ProductWizardStep.ProductHome);
  const [slotKeyToCustomize, setSlotKeyToCustomize] = useState('');
  const [manualModifications, setManualModifications] = useState<IUserManualModifications>({
    pickerAspects: false,
    comboSlot: false,
    modifiers: false,
  });
  const [userSelections, setUserSelections] = useState<IUserSelection>(() =>
    ProductWizardUtils.SetDefaultSelections({ menuObject, cartEntryOverrides: editingCartEntry })
  );
  const [selectedProduct, setSelectedProduct] = useState<Product>(() =>
    ProductWizardUtils.ComputeSelectedOption(menuObject, userSelections.pickerAspects)
  );
  const defaultComboSlotSelections = useMemo<UserSelectionComboSlot>(
    () =>
      ProductWizardUtils.SetDefaultSelections({
        menuObject,
        pickerSelectionOverrides: userSelections.pickerAspects,
      }).comboSlot || new Map(),
    [menuObject, userSelections.pickerAspects]
  );

  const enableMultiplierAsQuantity = useFlag(LaunchDarklyFlag.ENABLE_MULTIPLIER_AS_QUANTITY);

  /**
   * The menuObject may change when the plu price endpoint resolves
   * and because of that we must update the userSelections. Otherwise
   * we might end up keep showing unavailable products. In any case we
   * should prevent the user from even seeing the Menu while plu prices
   * are getting fetched but we added this extra logic here to be sure
   * the UI correctly reflect what we really have in menuObject. The only
   * case the following code might run into issues is when a customer
   * has already made an active selection when menuObject get changed
   * because in this case we are going to end up overriding the customer
   * selections. This case are going to be avoided by preventing the
   * customer to make any selections before plu prices are fully resolved.
   */
  useEffect(() => {
    /**
     * Check if menuObject really changed so we can apply new defaults.
     */
    const hasMenuObjectChanged = !isEqual(menuObject, prevMenuObjectRef.current);

    /**
     * Update the defaults if menuObject really changed.
     */
    if (hasMenuObjectChanged) {
      setUserSelections(current => {
        return ProductWizardUtils.SetDefaultSelections({
          menuObject,
          cartEntryOverrides: editingCartEntry,

          // Keep whatever user has previously selected.
          pickerSelectionOverrides: current.pickerAspects,
        });
      });
    }

    prevMenuObjectRef.current = menuObject;
  }, [menuObject, editingCartEntry]);

  useEffect(() => {
    if (!userSelections.comboSlot) {
      return;
    }
    const modifications = getUserModifications(
      userSelections.comboSlot,
      userSelections.modifiers,
      defaultComboSlotSelections,
      enableMultiplierAsQuantity
    );
    setManualModifications(prev => ({
      ...prev,
      comboSlot: prev.comboSlot || modifications.comboSlot,
      modifiers: prev.modifiers || modifications.modifiers,
    }));
  }, [
    defaultComboSlotSelections,
    enableMultiplierAsQuantity,
    userSelections.comboSlot,
    userSelections.modifiers,
  ]);

  const isDirty = useMemo<boolean>(() => {
    if (!userSelections.comboSlot) {
      return false;
    }
    const modifications = getUserModifications(
      userSelections.comboSlot,
      userSelections.modifiers,
      defaultComboSlotSelections,
      enableMultiplierAsQuantity
    );
    return modifications.comboSlot || modifications.modifiers;
  }, [
    defaultComboSlotSelections,
    enableMultiplierAsQuantity,
    userSelections.comboSlot,
    userSelections.modifiers,
  ]);

  const [productQuantity, setProductQuantity] = useState(
    editingCartEntry?.quantity || MIN_CART_QUANTITY
  );

  const itemToCustomize = useMemo<IItem | null>(
    () => userSelections.comboSlot?.get(slotKeyToCustomize)?.selectedItem || null,
    [slotKeyToCustomize, userSelections.comboSlot]
  );

  const updatePickerAspectSelections = useCallback(
    (args: UserSelectionPickerAspect | undefined) => {
      setManualModifications(prev => ({ ...prev, pickerAspects: true }));
      setUserSelections(current => {
        current.pickerAspects = args;
        return ProductWizardUtils.SetDefaultSelections({
          menuObject,
          pickerSelectionOverrides: current.pickerAspects,
        });
      });
    },
    [menuObject]
  );

  const goToHome = useCallback(() => {
    setCurrentStep(ProductWizardStep.ProductHome);
  }, []);

  const goToCustomizeProduct = useCallback((slotKey: string) => {
    setSlotKeyToCustomize(slotKey);
    setCurrentStep(ProductWizardStep.ProductCustomization);
  }, []);

  const submitCustomizations = useCallback<SubmitCustomizationsFn>(
    ({ modifiers, selectedItem, slotKey }) => {
      setUserSelections(current => {
        const comboSlot = current.comboSlot ? new Map(current.comboSlot) : new Map();
        comboSlot.set(slotKey, {
          comboSlot: current.comboSlot?.get(slotKey)?.comboSlot,
          selectedItem,
        });

        return {
          ...current,
          comboSlot,
          modifiers: {
            ...current.modifiers,
            [slotKey]: modifiers,
          },
        };
      });
    },
    []
  );

  useEffect(() => {
    setSelectedProduct(
      ProductWizardUtils.ComputeSelectedOption(menuObject, userSelections.pickerAspects)
    );
  }, [menuObject, userSelections.pickerAspects]);

  if (!selectedProduct) {
    return null;
  }

  return (
    <ProductWizardContext.Provider
      value={{
        currentStep,
        goToCustomizeProduct,
        goToHome,
        itemToCustomize,
        menuObject,
        productQuantity,
        selectedProduct,
        setProductQuantity,
        slotKeyToCustomize,
        submitCustomizations,
        updatePickerAspectSelections,
        userSelections,
        manualModifications,
        isDirty,
      }}
    >
      {children}
    </ProductWizardContext.Provider>
  );
};

export const useProductWizardContext = () => useContext(ProductWizardContext);
